import React, { useContext, useEffect, useMemo, useState } from 'react'
import ky from '../Modules/ApiClient'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDown, faAngleLeft, faAngleRight, faSpinner, faUser } from '@fortawesome/free-solid-svg-icons'
import { GlobalContext } from '../App'
import { API_URL } from '../Globals'

interface AuditChange {
  field: string
  before?: unknown
  after?: unknown
}

interface AuditEntry {
  _id: string
  createdAt: string
  actor: { username: string, firstName?: string, lastName?: string }
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'MERGE' | 'RESTORE'
  entityType: 'ADDRESS' | 'USER' | 'TASK'
  entityId: string
  summary: string
  changes: AuditChange[]
}

const actionLabels: Record<AuditEntry['action'], string> = {
  CREATE: 'Luotu',
  UPDATE: 'Muokattu',
  DELETE: 'Poistettu',
  MERGE: 'Yhdistetty',
  RESTORE: 'Palautettu'
}

const actionStyles: Record<AuditEntry['action'], string> = {
  CREATE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  UPDATE: 'bg-blue-50 text-blue-700 ring-blue-200',
  DELETE: 'bg-red-50 text-red-700 ring-red-200',
  MERGE: 'bg-violet-50 text-violet-700 ring-violet-200',
  RESTORE: 'bg-teal-50 text-teal-700 ring-teal-200'
}

const entityLabels: Record<AuditEntry['entityType'], string> = {
  ADDRESS: 'Kohde',
  USER: 'Käyttäjä',
  TASK: 'Työmerkintä'
}

const fieldLabels: Record<string, string> = {
  firstname: 'Etunimi',
  lastname: 'Sukunimi',
  firstName: 'Etunimi',
  lastName: 'Sukunimi',
  username: 'Käyttäjätunnus',
  address: 'Osoite',
  zip: 'Postinumero',
  city: 'Paikkakunta',
  phoneNumbers: 'Puhelinnumerot',
  additionalInfo: 'Lisätiedot',
  selectedFireplaceIds: 'Tulisijat',
  addressType: 'Osoitetyyppi',
  addressCategoryId: 'Osoitekategoria',
  lastSweepDate: 'Viimeisin nuohous',
  nextSweepDate: 'Seuraava nuohous',
  createdAt: 'Luontiaika',
  createdBy: 'Luonut',
  updatedAt: 'Muokkausaika',
  updatedBy: 'Muokannut',
  inactive: 'Arkistoitu',
  inactiveAt: 'Arkistointiaika',
  inactiveBy: 'Arkistoinut',
  mergeRecord: 'Yhdistämistiedot',
  permissions: 'Käyttöoikeudet',
  disabled: 'Poistettu käytöstä'
}

const valueText = (value: unknown): string => {
  if (value === undefined) return '—'
  if (value === null || value === '') return 'Tyhjä'
  if (typeof value === 'boolean') return value ? 'Kyllä' : 'Ei'
  if (Array.isArray(value) && value.length === 0) return 'Tyhjä'
  if (Array.isArray(value) && value.every(item => ['string', 'number'].includes(typeof item))) {
    return value.join(', ')
  }
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

const parsedDate = (value: string): Date | null => {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const dayKey = (value: string): string => {
  const date = parsedDate(value)
  return date ? [date.getFullYear(), date.getMonth(), date.getDate()].join('-') : 'unknown'
}

const dayLabel = (value: string): string => {
  const date = parsedDate(value)
  if (!date) return 'Tuntematon päivämäärä'

  const today = new Date()
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const tomorrowStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)
  const yesterdayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1)
  const prefix = date >= todayStart && date < tomorrowStart ? 'Tänään' : date >= yesterdayStart && date < todayStart ? 'Eilen' : ''
  const displayDate = date.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric', year: 'numeric' })
  return prefix ? `${prefix} · ${displayDate}` : displayDate
}

const AuditLog: React.FC = () => {
  const { globalVariable } = useContext(GlobalContext)
  const [logs, setLogs] = useState<AuditEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [error, setError] = useState('')
  const [actionFilter, setActionFilter] = useState('ALL')
  const [entityFilter, setEntityFilter] = useState('ALL')
  const perPage = 20

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')

    ky.get(`${API_URL}/api/audit`, {
      searchParams: { page, perPage },
      headers: {
        Authorization: `Bearer ${globalVariable.sessionToken || ''}`,
        Token: localStorage.getItem('token') || ''
      }
    }).json<{ logs: AuditEntry[], totalCount: number }>()
      .then(data => {
        if (cancelled) return
        setLogs(data.logs)
        setTotalCount(data.totalCount)
      })
      .catch(() => {
        if (!cancelled) setError('Tapahtumia ei voitu ladata. Tarkista käyttöoikeus ja palvelinyhteys.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [globalVariable.sessionToken, page])

  const visibleLogs = useMemo(() => logs.filter(entry => (
    (actionFilter === 'ALL' || entry.action === actionFilter) &&
    (entityFilter === 'ALL' || entry.entityType === entityFilter)
  )), [logs, actionFilter, entityFilter])

  const totalPages = Math.max(1, Math.ceil(totalCount / perPage))
  const filtersActive = actionFilter !== 'ALL' || entityFilter !== 'ALL'

  return (
    <main className="mobile-page container mx-auto mb-10 px-3 sm:px-6 lg:px-8">
      <header className="mobile-page-heading mb-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-2xl font-bold text-gray-900">Tapahtumat</h1>
          {!loading && !error && (
            <span className="text-xs font-medium tabular-nums text-gray-500">
              {totalCount.toLocaleString('fi-FI')} tapahtumaa yhteensä
            </span>
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:gap-3">
          <label className="min-w-0 text-xs font-semibold text-gray-600">
            Toiminto
            <select value={actionFilter} onChange={event => setActionFilter(event.target.value)} className="mt-1 block h-11 w-full min-w-0 rounded-xl border border-gray-300 bg-white px-2 text-sm font-normal text-gray-800 sm:px-3">
              <option value="ALL">Kaikki</option>
              <option value="CREATE">Luodut</option>
              <option value="UPDATE">Muokatut</option>
              <option value="DELETE">Poistetut</option>
              <option value="MERGE">Yhdistetyt</option>
              <option value="RESTORE">Palautetut</option>
            </select>
          </label>
          <label className="min-w-0 text-xs font-semibold text-gray-600">
            Kohdetyyppi
            <select value={entityFilter} onChange={event => setEntityFilter(event.target.value)} className="mt-1 block h-11 w-full min-w-0 rounded-xl border border-gray-300 bg-white px-2 text-sm font-normal text-gray-800 sm:px-3">
              <option value="ALL">Kaikki</option>
              <option value="ADDRESS">Kohteet</option>
              <option value="USER">Käyttäjät</option>
              <option value="TASK">Työmerkinnät</option>
            </select>
          </label>
        </div>
        {filtersActive && !loading && !error && (
          <p className="mt-2 text-xs text-gray-500" aria-live="polite">
            Näytetään {visibleLogs.length} / {logs.length} tämän sivun tapahtumaa. Rajaus koskee vain nykyistä sivua.
          </p>
        )}
      </header>

      {loading ? (
        <div role="status" className="flex items-center justify-center py-12 text-blue-600">
          <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />Ladataan tapahtumia…
        </div>
      ) : error ? (
        <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-5 text-center font-medium text-red-700">{error}</div>
      ) : visibleLogs.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-7 text-center text-sm text-gray-600">
          {filtersActive
            ? 'Tällä sivulla ei ole rajaukseen sopivia tapahtumia. Kokeile toista sivua tai muuta rajausta.'
            : 'Ei tapahtumia.'}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          {visibleLogs.map((entry, index) => {
            const actorName = [entry.actor?.firstName, entry.actor?.lastName].filter(Boolean).join(' ')
            const username = entry.actor?.username || 'Tuntematon'
            const sameActorName = actorName.toLocaleLowerCase('fi-FI') === username.toLocaleLowerCase('fi-FI')
            const changes = entry.changes || []
            const timestamp = parsedDate(entry.createdAt)
            const firstOfDay = index === 0 || dayKey(visibleLogs[index - 1].createdAt) !== dayKey(entry.createdAt)

            return (
              <React.Fragment key={entry._id}>
                {firstOfDay && (
                  <div className="border-b border-gray-100 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600 sm:px-4">
                    {dayLabel(entry.createdAt)}
                  </div>
                )}

                <article className="border-b border-gray-100 px-3 py-3 last:border-b-0 sm:px-4 sm:py-3.5">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-bold ring-1 ring-inset ${actionStyles[entry.action]}`}>
                      {actionLabels[entry.action]}
                    </span>
                    <span className="text-xs font-medium text-slate-500">{entityLabels[entry.entityType]}</span>
                    <time className="ml-auto whitespace-nowrap text-xs font-semibold tabular-nums text-slate-500" dateTime={entry.createdAt} title={timestamp?.toLocaleString('fi-FI') || ''}>
                      {timestamp?.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' }) || '—'}
                    </time>
                  </div>

                  <h2 className="mt-1.5 break-words text-sm font-semibold leading-5 text-gray-900">{entry.summary}</h2>
                  <p className="mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs text-slate-500">
                    <FontAwesomeIcon icon={faUser} className="text-[11px] text-slate-400" aria-hidden="true" />
                    <span className="break-words">{actorName || username}</span>
                    {actorName && !sameActorName && <span className="break-all text-slate-400">({username})</span>}
                  </p>

                  {changes.length > 0 && (
                    <details className="group mt-2 border-t border-slate-100 pt-1.5">
                      <summary className="inline-flex min-h-8 cursor-pointer list-none items-center gap-1.5 rounded-md py-1 text-xs font-semibold text-blue-700 hover:text-blue-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 [&::-webkit-details-marker]:hidden">
                        <FontAwesomeIcon icon={faAngleDown} className="text-[11px] transition-transform group-open:rotate-180" aria-hidden="true" />
                        <span className="group-open:hidden">Näytä muutokset ({changes.length})</span>
                        <span className="hidden group-open:inline">Piilota muutokset ({changes.length})</span>
                      </summary>
                      <div className="mt-1 divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-slate-50/50">
                        {changes.map((change, changeIndex) => (
                          <div key={`${change.field}-${changeIndex}`} className="grid gap-2 px-3 py-2.5 text-xs sm:grid-cols-[minmax(7rem,0.7fr)_minmax(0,1fr)_minmax(0,1fr)] sm:gap-3">
                            <strong className="break-words text-gray-800">{fieldLabels[change.field] || change.field}</strong>
                            <div className="min-w-0">
                              <span className="font-semibold text-slate-500">Ennen</span>
                              <p className="mt-0.5 max-h-36 overflow-auto whitespace-pre-wrap break-words text-slate-600">{valueText(change.before)}</p>
                            </div>
                            <div className="min-w-0">
                              <span className="font-semibold text-slate-500">Jälkeen</span>
                              <p className="mt-0.5 max-h-36 overflow-auto whitespace-pre-wrap break-words text-slate-900">{valueText(change.after)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </article>
              </React.Fragment>
            )
          })}
        </div>
      )}

      {!loading && !error && totalPages > 1 && (
        <nav className="mt-5 flex items-center justify-center gap-2" aria-label="Lokin sivutus">
          <button type="button" onClick={() => setPage(value => Math.max(1, value - 1))} disabled={page === 1} className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-gray-600 disabled:opacity-40" aria-label="Edellinen sivu">
            <FontAwesomeIcon icon={faAngleLeft} />
          </button>
          <span className="flex h-11 min-w-24 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-bold tabular-nums text-white">{page} / {totalPages}</span>
          <button type="button" onClick={() => setPage(value => Math.min(totalPages, value + 1))} disabled={page === totalPages} className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-gray-600 disabled:opacity-40" aria-label="Seuraava sivu">
            <FontAwesomeIcon icon={faAngleRight} />
          </button>
        </nav>
      )}
    </main>
  )
}

export default AuditLog
